import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Toast, Popconfirm, Form, Rating, Image, Tag } from '@douyinfe/semi-ui';
import { IconEdit, IconDelete, IconPlus, IconSearch, IconEyeOpened, IconSend } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';

const Comments = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showReplyModal, setShowReplyModal] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchScore, setSearchScore] = useState(null);
    const [formApi, setFormApi] = useState(null);
    const [replyFormApi, setReplyFormApi] = useState(null);

    const statusMap = {
        0: { text: '待审核', type: 'warning' },
        1: { text: '显示', type: 'success' },
        2: { text: '隐藏', type: 'danger' },
    };

    const scoreMap = {
        1: '1星',
        2: '2星',
        3: '3星',
        4: '4星',
        5: '5星',
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/shop/list_product_comment/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    score: searchScore === null ? -1 : searchScore
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleAdd = async (values) => {
        try {
            const response = await request.post('/go/sp/shop/add_product_comment/', values);
            if (response.errno === 0) {
                Toast.success('添加成功');
                setShowAddModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '添加失败');
            }
        } catch (error) {
            console.error('添加失败:', error);
            Toast.error('添加失败');
        }
    };

    const handleEdit = async (values) => {
        try {
            const response = await request.post('/go/sp/shop/edit_product_comment/', {
                ...values,
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('编辑成功');
                setShowEditModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '编辑失败');
            }
        } catch (error) {
            console.error('编辑失败:', error);
            Toast.error('编辑失败');
        }
    };

    const handleReply = async (values) => {
        try {
            const response = await request.post('/go/sp/shop/edit_product_comment/', {
                ...currentRecord,
                reply: values.reply,
                reply_time: new Date().getTime().toString(),
                uid: currentRecord.uid
            });
            if (response.errno === 0) {
                Toast.success('回复成功');
                setShowReplyModal(false);
                fetchList();
            } else {
                Toast.error(response.errstr || '回复失败');
            }
        } catch (error) {
            console.error('回复失败:', error);
            Toast.error('回复失败');
        }
    };

    const handleDelete = async (uids) => {
        try {
            const response = await request.post('/go/sp/shop/delete_product_comment/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids
            });
            if (response.errno === 0) {
                Toast.success('删除成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            Toast.error('删除失败');
        }
    };

    const handleBatchEdit = async (status, uids) => {
        if (!uids || (Array.isArray(uids) && !uids.length)) {
            Toast.warning('请选择要操作的评论');
            return;
        }
        try {
            const response = await request.post('/go/sp/shop/batchedit_product_comment/', {
                uids: Array.isArray(uids) ? uids.join(',') : uids,
                status
            });
            if (response.errno === 0) {
                Toast.success('操作成功');
                setSelectedRowKeys([]);
                fetchList();
            } else {
                Toast.error(response.errstr || '操作失败');
            }
        } catch (error) {
            console.error('操作失败:', error);
            Toast.error('操作失败');
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-';
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const renderImages = (images) => {
        if (!images) return '-';
        const imageList = images.split(';').filter(img => img.trim());
        if (imageList.length === 0) return '-';

        return (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {imageList.slice(0, 3).map((img, index) => (
                    <Image
                        key={index}
                        src={img}
                        width={40}
                        height={40}
                        style={{ borderRadius: 4 }}
                        preview={{
                            src: img
                        }}
                    />
                ))}
                {imageList.length > 3 && (
                    <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 4,
                        backgroundColor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: '#999'
                    }}>
                        +{imageList.length - 3}
                    </div>
                )}
            </div>
        );
    };

    const columns = [
        {
            title: '评论信息',
            dataIndex: 'brief',
            width: 300,
            render: (text, record) => (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ fontSize: '12px', flex: 1 }}>
                        <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>
                            {text || '-'}
                        </div>
                        <div style={{ color: 'var(--semi-color-text-2)', marginBottom: '4px' }}>
                            {record.su?.name || record.su_uid || '-'}
                        </div>
                        {record.product && (
                            <div style={{ color: 'var(--semi-color-text-2)' }}>
                                {record.product.name || '-'}
                                {(() => {
                                    if (!record.sku_uid) return '';
                                    const idx = record.sku_uid.indexOf(';');
                                    return idx !== -1 ? record.sku_uid.slice(idx) : '';
                                })()}
                            </div>
                        )}
                    </div>
                </div>
            )
        },
        {
            title: '评分',
            dataIndex: 'score',
            width: 220,
            render: (score) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Rating disabled value={score} size="small" />
                    <span style={{ fontSize: '12px', color: 'var(--semi-color-text-2)' }}>
                        {scoreMap[score] || '-'}
                    </span>
                </div>
            )
        },
        {
            title: '评论图片',
            dataIndex: 'images',
            width: 120,
            render: renderImages
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            render: (text) => {
                const status = statusMap[text] || { text: '异常', type: 'danger' };
                return <Tag color={status.type}>{status.text}</Tag>;
            }
        },
        {
            title: '商家回复',
            dataIndex: 'reply',
            width: 200,
            render: (text) => text || '-'
        },
        {
            title: '创建时间',
            dataIndex: 'create_time',
            width: 180,
            render: (text) => formatTimestamp(text)
        },
        {
            title: '操作',
            width: 200,
            render: (_, record) => (
                <Space>
                    <Button
                        type='tertiary'
                        icon={<IconEyeOpened />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowDetailModal(true);
                        }}
                    >
                        查看
                    </Button>
                    <Button
                        type='tertiary'
                        icon={<IconSend />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowReplyModal(true);
                        }}
                    >
                        回复
                    </Button>
                    <Button
                        type='primary'
                        icon={<IconEdit />}
                        onClick={() => {
                            setCurrentRecord(record);
                            setShowEditModal(true);
                        }}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除该评论吗？"
                        onConfirm={() => handleDelete(record.uid)}
                    >
                        <Button type='danger' icon={<IconDelete />}>删除</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const renderForm = (type = 'add') => {
        const isEdit = type === 'edit';
        const initialValues = isEdit && currentRecord ? {
            ...currentRecord,
            images: currentRecord.images ? currentRecord.images.split(';').join('\n') : '',
        } : {
            su_uid: '',
            order_uid: '',
            product_uid: '',
            sku_uid: '',
            status: 0,
            score: 5,
            brief: '',
            images: '',
            reply: '',
        };

        return (
            <Form
                getFormApi={setFormApi}
                initValues={initialValues}
                onSubmit={values => {
                    const submitValues = {
                        ...values,
                        images: values.images ? values.images.split('\n').filter(img => img.trim()).join(';') : '',
                        reply_time: values.reply ? new Date().getTime().toString() : '',
                    };
                    if (isEdit) {
                        handleEdit(submitValues);
                    } else {
                        handleAdd(submitValues);
                    }
                }}
                style={{ padding: '20px' }}
            >
                <Form.Input
                    field="su_uid"
                    label="顾客UID"
                    rules={[{ required: true, message: '请输入顾客UID' }]}
                />
                <Form.Input
                    field="order_uid"
                    label="订单UID"
                    rules={[{ required: true, message: '请输入订单UID' }]}
                />
                <Form.Input
                    field="product_uid"
                    label="产品UID"
                    rules={[{ required: true, message: '请输入产品UID' }]}
                />
                <Form.Input
                    field="sku_uid"
                    label="SKU属性"
                    placeholder="格式: product_uid;尺寸:X;颜色:红色"
                />
                <Form.Select
                    field="status"
                    label="状态"
                    initValue={0}
                >
                    <Select.Option value={0}>待审核</Select.Option>
                    <Select.Option value={1}>审核通过</Select.Option>
                    <Select.Option value={2}>审核拒绝</Select.Option>
                </Form.Select>
                <Form.Slot label="评分">
                    <Rating
                        value={formApi?.getValue('score') || 5}
                        onChange={value => formApi?.setValue('score', value)}
                    />
                </Form.Slot>
                <Form.TextArea
                    field="brief"
                    label="评论内容"
                    rules={[{ required: true, message: '请输入评论内容' }]}
                    rows={4}
                />
                <Form.TextArea
                    field="images"
                    label="评论图片"
                    placeholder="每行一个图片URL"
                    rows={3}
                />
                <Form.TextArea
                    field="reply"
                    label="商家回复"
                    rows={3}
                />
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    const renderReplyForm = () => {
        if (!currentRecord) return null;

        return (
            <Form
                getFormApi={setReplyFormApi}
                initValues={{
                    reply: currentRecord.reply || ''
                }}
                onSubmit={handleReply}
                style={{ padding: '20px' }}
            >
                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <div style={{ fontSize: '14px', color: 'var(--semi-color-text-2)', marginBottom: '8px' }}>评论内容:</div>
                    <div style={{ fontSize: '14px' }}>{currentRecord.brief || '-'}</div>
                </div>
                <Form.TextArea
                    field="reply"
                    label="商家回复"
                    placeholder="请输入回复内容"
                    rules={[{ required: true, message: '请输入回复内容' }]}
                    rows={4}
                />
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                    <Space>
                        <Button type='tertiary' onClick={() => setShowReplyModal(false)}>取消</Button>
                        <Button type='primary' htmlType="submit">确定回复</Button>
                    </Space>
                </div>
            </Form>
        );
    };

    const renderDetail = () => {
        if (!currentRecord) return null;

        return (
            <div style={{ padding: '20px' }}>
                <div style={{ marginBottom: '16px' }}>
                    <h4>评论详情</h4>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', fontSize: '14px' }}>
                    <div style={{ color: 'var(--semi-color-text-2)' }}>评论ID:</div>
                    <div>{currentRecord.uid}</div>

                    <div style={{ color: 'var(--semi-color-text-2)' }}>顾客:</div>
                    <div>{currentRecord.su?.name || currentRecord.su_uid || '-'}</div>

                    <div style={{ color: 'var(--semi-color-text-2)' }}>商品:</div>
                    <div>{currentRecord.product?.name || '-'}</div>

                    <div style={{ color: 'var(--semi-color-text-2)' }}>评分:</div>
                    <div>
                        <Rating disabled value={currentRecord.score} size="small" />
                        <span style={{ marginLeft: '8px' }}>{scoreMap[currentRecord.score] || '-'}</span>
                    </div>

                    <div style={{ color: 'var(--semi-color-text-2)' }}>状态:</div>
                    <div>
                        <Tag color={statusMap[currentRecord.status]?.type || 'default'}>
                            {statusMap[currentRecord.status]?.text || '-'}
                        </Tag>
                    </div>

                    <div style={{ color: 'var(--semi-color-text-2)' }}>评论内容:</div>
                    <div>{currentRecord.brief || '-'}</div>

                    <div style={{ color: 'var(--semi-color-text-2)' }}>评论图片:</div>
                    <div>{renderImages(currentRecord.images)}</div>

                    <div style={{ color: 'var(--semi-color-text-2)' }}>商家回复:</div>
                    <div>{currentRecord.reply || '-'}</div>

                    <div style={{ color: 'var(--semi-color-text-2)' }}>回复时间:</div>
                    <div>{formatTimestamp(currentRecord.reply_time)}</div>

                    <div style={{ color: 'var(--semi-color-text-2)' }}>创建时间:</div>
                    <div>{formatTimestamp(currentRecord.create_time)}</div>

                    <div style={{ color: 'var(--semi-color-text-2)' }}>更新时间:</div>
                    <div>{formatTimestamp(currentRecord.update_time)}</div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.commentsContainer}>
            <div className={styles.header}>
                <Space>
                    <Input
                        placeholder="搜索评论内容"
                        value={searchKey}
                        onChange={setSearchKey}
                        style={{ width: 200 }}
                        showClear
                    />
                    <Select
                        placeholder="评论状态"
                        value={searchStatus}
                        onChange={setSearchStatus}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部状态</Select.Option>
                        {Object.entries(statusMap).map(([value, { text }]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Select
                        placeholder="评分筛选"
                        value={searchScore}
                        onChange={setSearchScore}
                        style={{ width: 150 }}
                        showClear
                    >
                        <Select.Option value={null}>全部评分</Select.Option>
                        {Object.entries(scoreMap).map(([value, text]) => (
                            <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => fetchList(1, pagination.pageSize)}
                        loading={loading}
                        icon={<IconSearch />}
                    >
                        搜索
                    </Button>
                    <Button type='primary' icon={<IconPlus />} onClick={() => setShowAddModal(true)}>新增评论</Button>
                    {selectedRowKeys.length > 0 && (
                        <Space>
                            <Button type='success' onClick={() => handleBatchEdit(1, selectedRowKeys)}>批量显示</Button>
                            <Button type='danger' onClick={() => handleBatchEdit(2, selectedRowKeys)}>批量隐藏</Button>
                            <Popconfirm
                                title={`确定要删除选中的 ${selectedRowKeys.length} 个评论吗？`}
                                onConfirm={() => handleDelete(selectedRowKeys)}
                            >
                                <Button type='danger' icon={<IconDelete />}>批量删除</Button>
                            </Popconfirm>
                        </Space>
                    )}
                </Space>
            </div>

            <Table
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onPageChange: (page) => fetchList(page, pagination.pageSize),
                    onPageSizeChange: (size) => fetchList(1, size)
                }}
                rowKey="uid"
                rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys
                }}
            />

            <Modal
                title="新增评论"
                visible={showAddModal}
                onCancel={() => setShowAddModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('add')}
            </Modal>

            <Modal
                title="编辑评论"
                visible={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
                centered
                width={700}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderForm('edit')}
            </Modal>

            <Modal
                title="评论详情"
                visible={showDetailModal}
                onCancel={() => setShowDetailModal(false)}
                footer={null}
                centered
                width={600}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderDetail()}
            </Modal>

            <Modal
                title="回复评论"
                visible={showReplyModal}
                onCancel={() => setShowReplyModal(false)}
                footer={null}
                centered
                width={600}
                bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                style={{ '& ::-webkit-scrollbar': { display: 'none' } }}
            >
                {renderReplyForm()}
            </Modal>
        </div>
    );
};

export default Comments; 